// AI Support Team — Super Admin panels (Phase 2: safe auto-replies, known issues, avatars).
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Sparkles, Loader2, ChevronDown, ChevronUp, ShieldAlert, Hand, RefreshCw, Camera, Bug, Plus, Megaphone, Link2, X, Zap } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const AGENT_STYLE = {
  kiri: "bg-[#FF5F15] text-white", mack: "bg-zinc-800 text-white", tui: "bg-emerald-600 text-white",
  finn: "bg-sky-600 text-white", scout: "bg-amber-600 text-white", dex: "bg-violet-600 text-white",
};
const SEV_CLS = {
  P1: "bg-red-600 text-white", P2: "bg-orange-500 text-white",
  P3: "bg-zinc-200 text-zinc-800 border border-zinc-300", P4: "bg-zinc-100 text-zinc-600 border border-zinc-200",
};
const AVATAR_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/ai-support/avatars`;
const Avatar = ({ agent, size = "w-9 h-9 text-sm", v }) => {
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [agent, v]);
  if (!err && agent) {
    return <img src={`${AVATAR_BASE}/${agent}${v ? `?v=${encodeURIComponent(v)}` : ""}`} alt={agent}
      onError={() => setErr(true)} className={`${size} rounded-full object-cover shrink-0 border border-zinc-200 bg-zinc-100`} />;
  }
  return (
    <span className={`${size} rounded-full flex items-center justify-center font-display font-black shrink-0 ${AGENT_STYLE[agent] || "bg-zinc-300"}`}>
      {(agent || "?").charAt(0).toUpperCase()}
    </span>
  );
};

// ================= Team panel (Support page header, super admin) =================
export function AiSupportTeamPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/platform/ai-support/overview"); setData(data); } catch {}
  };
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const { settings, agents, stats, knowledge_gaps } = data;
  const toggleMaster = async () => {
    const v = !settings.master_enabled;
    await api.patch("/platform/ai-support/settings", { master_enabled: v });
    toast.success(v ? "AI Support Team ON" : "AI Support Team OFF — support continues normally");
    load();
  };
  const toggleAgent = async (k) => {
    await api.patch("/platform/ai-support/settings", { agents: { [k]: { enabled: !settings.agents[k].enabled } } });
    load();
  };
  return (
    <div className="hard-card p-4 mb-5" data-testid="ai-support-team-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <Bot className="w-5 h-5 text-[#FF5F15]" />
        <span className="font-display font-black uppercase tracking-tight">AI Support Team</span>
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-zinc-100 border border-zinc-200 text-zinc-600">Phase 2 · Safe auto-replies</span>
        <span className="flex items-center gap-2 ml-auto">
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{settings.master_enabled ? "ON" : "OFF"}</span>
          <Switch checked={settings.master_enabled} onCheckedChange={toggleMaster} data-testid="ai-master-toggle" />
          <button onClick={() => setOpen((o) => !o)} className="text-zinc-500 hover:text-zinc-900 p-1" data-testid="ai-team-expand">
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </span>
      </div>
      <div className="flex gap-4 flex-wrap mt-3 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
        <span><b className="text-zinc-900">{stats.open}</b> open</span>
        <span><b className={stats.needs_human ? "text-orange-600" : "text-zinc-900"}>{stats.needs_human}</b> need human</span>
        <span><b className={stats.critical ? "text-red-600" : "text-zinc-900"}>{stats.critical}</b> critical</span>
        <span><b className="text-zinc-900">{stats.drafts_ready}</b> drafts ready</span>
        <span><b className="text-zinc-900">{stats.auto_replied || 0}</b> auto-replied</span>
      </div>
      {open && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-4" data-testid="ai-agent-cards">
            {Object.entries(agents).map(([k, a]) => (
              <AgentCard key={k} k={k} a={a} cfg={settings.agents[k] || {}} onToggle={() => toggleAgent(k)} onChanged={load} />
            ))}
          </div>
          {knowledge_gaps?.length > 0 && (
            <div className="mt-4 border-t border-zinc-200 pt-3" data-testid="ai-knowledge-gaps">
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Knowledge gaps (top asked)</div>
              {knowledge_gaps.slice(0, 5).map((g) => (
                <div key={g.question} className="text-[12px] text-zinc-700 flex justify-between gap-3 py-0.5">
                  <span className="truncate">{g.question}</span><b>{g.count}</b>
                </div>
              ))}
            </div>
          )}
          <ApprovedAnswers />
        </>
      )}
    </div>
  );
}

// ================= Approved answers (grounds Kiri's auto-replies) =================
function ApprovedAnswers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", module: "Other" });
  const [adding, setAdding] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/platform/ai-support/knowledge"); setRows(data.knowledge || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.question.trim() || !form.answer.trim()) return toast.error("Question and answer are required");
    await api.post("/platform/ai-support/knowledge", form);
    toast.success("Approved answer added — Kiri can now use it");
    setForm({ question: "", answer: "", module: "Other" });
    setAdding(false);
    load();
  };
  const remove = async (id) => {
    await api.delete(`/platform/ai-support/knowledge/${id}`);
    load();
  };
  return (
    <div className="mt-4 border-t border-zinc-200 pt-3" data-testid="ai-approved-answers">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Approved answers ({rows.length}) — Kiri only auto-replies from these</div>
        <button onClick={() => setAdding((a) => !a)} className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] hover:underline inline-flex items-center gap-1" data-testid="aa-add-btn"><Plus className="w-3 h-3" /> Add answer</button>
      </div>
      {adding && (
        <div className="mt-2 border border-zinc-200 rounded-sm p-2.5 bg-zinc-50/60 space-y-2" data-testid="aa-form">
          <input value={form.question} onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
            placeholder="Question — e.g. How do I add a team member?"
            className="w-full border border-zinc-300 rounded-sm px-2.5 py-1.5 text-sm" data-testid="aa-question-input" />
          <textarea value={form.answer} onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))} rows={3}
            placeholder="The approved answer Kiri may send…"
            className="w-full border border-zinc-300 rounded-sm px-2.5 py-1.5 text-sm" data-testid="aa-answer-input" />
          <div className="flex gap-2">
            <select value={form.module} onChange={(e) => setForm((f) => ({ ...f, module: e.target.value }))}
              className="border border-zinc-300 rounded-sm px-2 py-1.5 text-xs" data-testid="aa-module-select">
              {KI_MODULES.map((m) => <option key={m}>{m}</option>)}
            </select>
            <button onClick={add} className="text-[10px] font-bold uppercase tracking-wider bg-zinc-900 text-white px-3 py-1.5 rounded-sm hover:bg-zinc-700 ml-auto" data-testid="aa-save-btn">Save answer</button>
          </div>
        </div>
      )}
      {rows.slice(0, 8).map((k) => (
        <div key={k.id} className="text-[12px] text-zinc-700 flex items-start justify-between gap-3 py-1 border-b border-zinc-100" data-testid={`aa-row-${k.id}`}>
          <span className="min-w-0"><b>{k.question}</b><span className="block text-zinc-500 truncate">{k.answer}</span></span>
          <button onClick={() => remove(k.id)} className="text-zinc-400 hover:text-red-500 shrink-0 mt-0.5" data-testid={`aa-delete-${k.id}`}><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
}

// ================= Agent card (avatar upload + Kiri auto-reply controls) =================
function AgentCard({ k, a, cfg, onToggle, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/platform/ai-support/agents/${k}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${a.name}'s avatar updated`);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Avatar upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const setAutoReply = async (v) => {
    await api.patch("/platform/ai-support/settings", { agents: { [k]: { auto_reply: v } } });
    toast.success(v ? "Kiri will auto-answer approved how-to questions" : "Auto-reply off — drafts only");
    onChanged?.();
  };
  const setThreshold = async (v) => {
    const t = Math.max(50, Math.min(100, Number(v) || 90));
    await api.patch("/platform/ai-support/settings", { agents: { [k]: { threshold: t } } });
    onChanged?.();
  };
  return (
    <div className="border border-zinc-200 rounded-sm p-3" data-testid={`ai-agent-card-${k}`}>
      <div className="flex items-center gap-3">
        <div className="relative shrink-0 group">
          <Avatar agent={k} size="w-11 h-11 text-sm" v={a.avatar_v} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-zinc-900 text-white flex items-center justify-center hover:bg-[#FF5F15] transition-colors"
            title="Upload avatar" data-testid={`ai-avatar-upload-${k}`}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadAvatar} data-testid={`ai-avatar-input-${k}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">{a.name} <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 ml-1">{a.kind === "internal" ? "Internal" : "Customer-facing"}</span></div>
          <div className="text-[11px] text-zinc-500">{a.role} · {a.tickets} tickets{a.avg_confidence ? ` · ${a.avg_confidence}% avg conf` : ""}</div>
        </div>
        <Switch checked={cfg.enabled !== false} onCheckedChange={onToggle} data-testid={`ai-agent-toggle-${k}`} />
      </div>
      {k === "kiri" ? (
        <div className="mt-2.5 border-t border-zinc-100 pt-2 flex items-center gap-2 flex-wrap" data-testid="kiri-auto-reply-controls">
          <Zap className={`w-3.5 h-3.5 ${cfg.auto_reply ? "text-[#FF5F15]" : "text-zinc-300"}`} />
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-600">Auto-answer how-to</span>
          <Switch checked={!!cfg.auto_reply} onCheckedChange={setAutoReply} data-testid="kiri-auto-reply-toggle" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 ml-auto">≥</span>
          <input type="number" min="50" max="100" defaultValue={cfg.threshold ?? 90} onBlur={(e) => setThreshold(e.target.value)}
            className="w-14 border border-zinc-300 rounded-sm px-1.5 py-0.5 text-xs font-mono text-right" data-testid="kiri-threshold-input" />
          <span className="text-[10px] font-mono text-zinc-400">% conf</span>
        </div>
      ) : (
        <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">Auto-reply: drafts only</div>
      )}
    </div>
  );
}

// ================= Known issues board =================
const KI_MODULES = ["Jobs", "Timesheets", "Payroll", "Fleet", "Plant", "Clients", "Quotes", "Invoices", "Estimating", "Takeoffs", "QA", "Safety", "Documents", "Business Profiles", "Contractor Directory", "Civil Hub", "Support", "Xero", "Email", "Authentication", "Other"];
const KI_STATUS_CLS = {
  open: "bg-red-50 text-red-700 border-red-200",
  investigating: "bg-amber-50 text-amber-700 border-amber-300",
  fixed: "bg-emerald-50 text-emerald-700 border-emerald-300",
};
export function KnownIssuesBoard() {
  const [issues, setIssues] = useState(null);
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", module: "Other", severity: "P3", description: "" });
  const load = async () => {
    try { const { data } = await api.get("/platform/ai-support/known-issues"); setIssues(data.issues || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  if (issues === null) return null;
  const active = issues.filter((i) => i.status !== "fixed").length;
  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    await api.post("/platform/ai-support/known-issues", form);
    toast.success("Known issue added");
    setForm({ title: "", module: "Other", severity: "P3", description: "" });
    setShowForm(false);
    load();
  };
  const setStatus = async (kid, status) => {
    await api.patch(`/platform/ai-support/known-issues/${kid}`, { status });
    toast.success(status === "fixed" ? "Marked fixed — you can now notify linked customers" : `Status: ${status}`);
    load();
  };
  const notifyFixed = async (kid) => {
    try {
      const { data } = await api.post(`/platform/ai-support/known-issues/${kid}/notify-fixed`);
      toast.success(`Mack notified ${data.notified} customer${data.notified === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Notify failed"); }
  };
  const removeIssue = async (kid) => {
    await api.delete(`/platform/ai-support/known-issues/${kid}`);
    toast.success("Known issue deleted");
    load();
  };
  return (
    <div className="hard-card p-4 mb-5" data-testid="known-issues-board">
      <div className="flex items-center gap-3 flex-wrap">
        <Bug className="w-5 h-5 text-[#FF5F15]" />
        <span className="font-display font-black uppercase tracking-tight">Known Issues</span>
        <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{active} active · {issues.length} total</span>
        <span className="flex items-center gap-1.5 ml-auto">
          <button onClick={() => { setShowForm((s) => !s); setOpen(true); }}
            className="text-[10px] font-bold uppercase tracking-wider bg-[#FF5F15] hover:bg-[#E65100] text-white px-2 py-1 rounded-sm inline-flex items-center gap-1" data-testid="ki-new-btn">
            <Plus className="w-3 h-3" /> New issue
          </button>
          <button onClick={() => setOpen((o) => !o)} className="text-zinc-500 hover:text-zinc-900 p-1" data-testid="ki-expand">
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </span>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          {showForm && (
            <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/60 space-y-2" data-testid="ki-form">
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Issue title — e.g. Timesheet PDF export missing weekend rows"
                className="w-full border border-zinc-300 rounded-sm px-2.5 py-1.5 text-sm" data-testid="ki-title-input" />
              <div className="flex gap-2 flex-wrap">
                <select value={form.module} onChange={(e) => setForm((f) => ({ ...f, module: e.target.value }))}
                  className="border border-zinc-300 rounded-sm px-2 py-1.5 text-xs" data-testid="ki-module-select">
                  {KI_MODULES.map((m) => <option key={m}>{m}</option>)}
                </select>
                <select value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                  className="border border-zinc-300 rounded-sm px-2 py-1.5 text-xs" data-testid="ki-severity-select">
                  {["P1", "P2", "P3", "P4"].map((s) => <option key={s}>{s}</option>)}
                </select>
                <button onClick={create} className="text-[10px] font-bold uppercase tracking-wider bg-zinc-900 text-white px-3 py-1.5 rounded-sm hover:bg-zinc-700 ml-auto" data-testid="ki-create-btn">Add issue</button>
              </div>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={2} placeholder="What's broken, who it affects, workaround if any…"
                className="w-full border border-zinc-300 rounded-sm px-2.5 py-1.5 text-sm" data-testid="ki-description-input" />
            </div>
          )}
          {issues.length === 0 && <p className="text-xs text-zinc-400 italic">No known issues on the register.</p>}
          {issues.map((i) => (
            <div key={i.id} className="border border-zinc-200 rounded-sm p-3" data-testid={`ki-issue-${i.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${SEV_CLS[i.severity] || SEV_CLS.P3}`}>{i.severity}</span>
                <span className="font-bold text-sm">{i.title}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-white border border-zinc-200">{i.module}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${KI_STATUS_CLS[i.status]}`} data-testid={`ki-status-${i.id}`}>{i.status}</span>
                <span className="text-[11px] font-mono text-zinc-500 ml-auto">{(i.linked_ticket_ids || []).length} linked ticket{(i.linked_ticket_ids || []).length === 1 ? "" : "s"}</span>
              </div>
              {i.description && <p className="text-[12px] text-zinc-600 mt-1.5">{i.description}</p>}
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {["open", "investigating", "fixed"].filter((s) => s !== i.status).map((s) => (
                  <button key={s} onClick={() => setStatus(i.id, s)}
                    className="text-[10px] font-bold uppercase tracking-wider border border-zinc-300 bg-white px-2 py-1 rounded-sm hover:border-zinc-500" data-testid={`ki-set-${s}-${i.id}`}>
                    Mark {s}
                  </button>
                ))}
                {i.status === "fixed" && (i.linked_ticket_ids || []).length > 0 && (
                  <button onClick={() => notifyFixed(i.id)}
                    className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded-sm inline-flex items-center gap-1" data-testid={`ki-notify-${i.id}`}>
                    <Megaphone className="w-3 h-3" /> {i.notified_at ? "Notify again" : "Notify customers"}
                  </button>
                )}
                {i.notified_at && <span className="text-[10px] font-mono text-emerald-700">Customers notified</span>}
                <button onClick={() => removeIssue(i.id)} className="text-zinc-400 hover:text-red-500 ml-auto" title="Delete issue" data-testid={`ki-delete-${i.id}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Link a technical ticket to a known issue (Mack workflow)
function KnownIssueLinker({ ticket, onChanged }) {
  const [issues, setIssues] = useState(null);
  const [picking, setPicking] = useState(false);
  const loadIssues = async () => {
    const { data } = await api.get("/platform/ai-support/known-issues");
    setIssues((data.issues || []).filter((i) => i.status !== "fixed"));
  };
  const link = async (kid) => {
    await api.post(`/platform/ai-support/known-issues/${kid}/link`, { ticket_id: ticket.id });
    toast.success("Linked to known issue");
    setPicking(false);
    onChanged?.();
  };
  const unlink = async () => {
    await api.post(`/platform/ai-support/known-issues/${ticket.ai_known_issue_id}/unlink`, { ticket_id: ticket.id });
    toast.success("Unlinked");
    onChanged?.();
  };
  const createFromTicket = async () => {
    await api.post("/platform/ai-support/known-issues", {
      title: ticket.subject, module: ticket.ai_module, severity: ticket.ai_severity,
      description: ticket.ai_summary || "", ticket_id: ticket.id,
    });
    toast.success("Known issue created from this ticket");
    onChanged?.();
  };
  if (ticket.ai_known_issue_id) {
    return (
      <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="ki-linked-chip">
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-zinc-800 text-white inline-flex items-center gap-1">
          <Bug className="w-3 h-3" /> Known issue: {ticket.ai_known_issue_title}
        </span>
        <button onClick={unlink} className="text-zinc-400 hover:text-red-500" title="Unlink" data-testid="ki-unlink-btn"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="ki-linker">
      {(ticket.ai_suggested_issues || []).map((s) => (
        <button key={s.id} onClick={() => link(s.id)}
          className="text-[10px] font-bold uppercase tracking-wider border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1 rounded-sm hover:border-amber-500 inline-flex items-center gap-1"
          data-testid={`ki-suggest-${s.id}`}>
          <Link2 className="w-3 h-3" /> Link: {s.title}
        </button>
      ))}
      {picking ? (
        <select onChange={(e) => e.target.value && link(e.target.value)} defaultValue=""
          className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white" data-testid="ki-pick-select">
          <option value="" disabled>Pick a known issue…</option>
          {(issues || []).map((i) => <option key={i.id} value={i.id}>{i.severity} · {i.title}</option>)}
        </select>
      ) : (
        <button onClick={() => { setPicking(true); loadIssues(); }}
          className="text-[10px] font-bold uppercase tracking-wider border border-zinc-300 bg-white px-2 py-1 rounded-sm hover:border-zinc-500 inline-flex items-center gap-1" data-testid="ki-link-btn">
          <Link2 className="w-3 h-3" /> Link known issue
        </button>
      )}
      <button onClick={createFromTicket}
        className="text-[10px] font-bold uppercase tracking-wider border border-zinc-300 bg-white px-2 py-1 rounded-sm hover:border-zinc-500 inline-flex items-center gap-1" data-testid="ki-create-from-ticket-btn">
        <Plus className="w-3 h-3" /> New known issue
      </button>
    </div>
  );
}

// ================= Per-ticket AI analysis block =================
export function AiAnalysisBlock({ ticket, onUseDraft, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const analyze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/ai-support/tickets/${ticket.id}/analyze`);
      if (data.ok) { toast.success("AI analysis complete"); onChanged?.(); }
      else toast.error(data.detail || "AI unavailable");
    } catch (e) { toast.error(e.response?.data?.detail || "AI unavailable"); }
    finally { setBusy(false); }
  };
  const takeover = async () => {
    await api.post(`/platform/ai-support/tickets/${ticket.id}/takeover`);
    toast.success("Human takeover — AI drafting paused for this ticket");
    onChanged?.();
  };
  const loadTimeline = async () => {
    if (!showTimeline) {
      const { data } = await api.get(`/platform/ai-support/tickets/${ticket.id}/events`);
      setEvents(data.events || []);
    }
    setShowTimeline((s) => !s);
  };
  const agent = ticket.ai_agent;
  return (
    <div className="border border-zinc-200 rounded-sm bg-zinc-50/60 p-3.5 mb-3" data-testid="ai-analysis-block">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="w-4 h-4 text-[#FF5F15]" />
        <span className="text-[11px] font-black uppercase tracking-widest text-zinc-700">AI Support Analysis</span>
        {ticket.ai_human_takeover && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-zinc-800 text-white">Human support active</span>}
        {ticket.ai_auto_replied && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white inline-flex items-center gap-1" data-testid="ai-auto-replied-badge"><Zap className="w-3 h-3" /> Auto-replied</span>}
        <span className="ml-auto flex items-center gap-1.5">
          {!ticket.ai_human_takeover && (
            <button onClick={takeover} className="text-[10px] font-bold uppercase tracking-wider border border-zinc-300 bg-white px-2 py-1 rounded-sm hover:border-zinc-500 inline-flex items-center gap-1" data-testid="ai-takeover-btn"><Hand className="w-3 h-3" /> Take over</button>
          )}
          <button onClick={analyze} disabled={busy} className="text-[10px] font-bold uppercase tracking-wider bg-[#FF5F15] hover:bg-[#E65100] text-white px-2 py-1 rounded-sm inline-flex items-center gap-1" data-testid="ai-analyze-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {agent ? "Re-analyse" : "Analyse with AI"}
          </button>
        </span>
      </div>
      {ticket.ai_status === "unavailable" && <p className="text-[12px] text-zinc-500 mt-2">AI analysis unavailable — support continues normally.</p>}
      {agent && (
        <>
          <div className="flex items-center gap-2.5 mt-3 flex-wrap">
            <Avatar agent={agent} size="w-8 h-8 text-xs" />
            <div>
              <div className="text-sm font-bold capitalize">{agent} <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 ml-1">Fieldlink AI Support Assistant</span></div>
            </div>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${SEV_CLS[ticket.ai_severity] || SEV_CLS.P3}`}>{ticket.ai_severity}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-white border border-zinc-200">{ticket.ai_category}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-white border border-zinc-200">{ticket.ai_module}</span>
            <span className="text-[10px] font-mono text-zinc-500">{ticket.ai_confidence}% confidence</span>
            {ticket.ai_matching_tickets > 0 && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 border border-amber-300 text-amber-800">{ticket.ai_matching_tickets} similar</span>}
          </div>
          {ticket.ai_severity === "P1" && (
            <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-300 text-red-700 rounded-sm px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider" data-testid="ai-p1-banner">
              <ShieldAlert className="w-4 h-4" /> Critical support incident — human review required
            </div>
          )}
          {ticket.ai_summary && <p className="text-[12.5px] text-zinc-700 mt-2" data-testid="ai-summary"><b>Summary:</b> {ticket.ai_summary}</p>}
          {ticket.ai_recommended_action && <p className="text-[12px] text-zinc-600 mt-1"><b>Recommended:</b> {ticket.ai_recommended_action}</p>}
          {ticket.ai_knowledge_gap && <p className="text-[11px] text-amber-700 mt-1 font-semibold">Knowledge gap — verify before sending.</p>}
          {["BUG", "CRASH", "PERFORMANCE"].includes(ticket.ai_category) && <KnownIssueLinker ticket={ticket} onChanged={onChanged} />}
          {ticket.ai_draft && !ticket.ai_human_requested && (
            <div className="mt-3 bg-white border border-zinc-200 rounded-sm p-3" data-testid="ai-draft-box">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Draft response — human approval required</span>
                <button onClick={() => onUseDraft?.(ticket.ai_draft)} className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] hover:underline" data-testid="ai-use-draft-btn">Use draft</button>
              </div>
              <p className="text-[12.5px] text-zinc-700 whitespace-pre-line mt-1.5 max-h-44 overflow-y-auto">{ticket.ai_draft}</p>
            </div>
          )}
          {ticket.ai_human_requested && <p className="text-[11px] text-zinc-600 mt-2 font-semibold">Customer asked for a human — AI drafting stopped.</p>}
        </>
      )}
      <button onClick={loadTimeline} className="mt-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-800" data-testid="ai-timeline-toggle">
        {showTimeline ? "Hide timeline" : "View AI timeline"}
      </button>
      {showTimeline && (
        <div className="mt-2 space-y-1" data-testid="ai-timeline">
          {(events || []).map((e) => (
            <div key={e.id} className="flex gap-2 text-[11px] text-zinc-600">
              <span className="font-mono text-zinc-400 shrink-0">{new Date(e.at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}</span>
              <span className={`font-bold uppercase text-[9px] tracking-wider px-1 rounded-sm self-start mt-[1px] ${e.actor === "ai" ? "bg-[#FF5F15]/10 text-[#FF5F15]" : e.actor === "human" ? "bg-zinc-800 text-white" : "bg-zinc-200 text-zinc-600"}`}>{e.actor}</span>
              <span>{e.agent ? <b className="capitalize">{e.agent}: </b> : null}{e.detail || e.action}</span>
            </div>
          ))}
          {events && events.length === 0 && <p className="text-[11px] text-zinc-400">No AI events yet.</p>}
        </div>
      )}
    </div>
  );
}
